/**
 * Error Types
 * Generated from data-model.md
 */

// =============================================================================
// API Error Types
// =============================================================================

/**
 * Standard API error response
 */
export interface ApiError {
  /** Error code (e.g., 'AUTH_INVALID_CREDENTIALS') */
  code: string;
  /** Human-readable message */
  message: string;
  /** Field that caused error (for validation) */
  field?: string;
  /** Additional error details */
  details?: Record<string, unknown>;
}

/**
 * Authentication error codes
 */
export type AuthErrorCode =
  | 'AUTH_INVALID_CREDENTIALS'
  | 'AUTH_MAGIC_LINK_EXPIRED'
  | 'AUTH_MAGIC_LINK_INVALID'
  | 'AUTH_SESSION_EXPIRED'
  | 'AUTH_UNAUTHORIZED';

/**
 * Validation error codes
 */
export type ValidationErrorCode =
  | 'VALIDATION_REQUIRED'
  | 'VALIDATION_INVALID_FORMAT'
  | 'VALIDATION_OUT_OF_RANGE'
  | 'VALIDATION_MIN_LENGTH'
  | 'VALIDATION_MAX_LENGTH'
  | 'VALIDATION_PATTERN_MISMATCH';

/**
 * Analysis error codes
 */
export type AnalysisErrorCode =
  | 'ANALYSIS_NOT_FOUND'
  | 'ANALYSIS_PROCESSING_FAILED'
  | 'ANALYSIS_ALREADY_PREMIUM';

/**
 * Network error codes
 */
export type NetworkErrorCode =
  | 'NETWORK_ERROR'
  | 'NETWORK_TIMEOUT'
  | 'NETWORK_OFFLINE';

/**
 * All error codes
 */
export type ErrorCode =
  | AuthErrorCode
  | ValidationErrorCode
  | AnalysisErrorCode
  | NetworkErrorCode;

// =============================================================================
// Error Classes
// =============================================================================

/**
 * Base application error class
 */
export class AppError extends Error {
  public readonly code: ErrorCode;
  public readonly field?: string;
  public readonly details?: Record<string, unknown>;

  constructor(
    code: ErrorCode,
    message: string,
    field?: string,
    details?: Record<string, unknown>
  ) {
    super(message);
    this.name = 'AppError';
    this.code = code;
    this.field = field;
    this.details = details;
  }

  /**
   * Create an AppError from an ApiError response
   */
  static fromApiError(apiError: ApiError): AppError {
    return new AppError(
      apiError.code as ErrorCode,
      apiError.message,
      apiError.field,
      apiError.details
    );
  }

  /**
   * Convert to ApiError format for consistency
   */
  toApiError(): ApiError {
    return {
      code: this.code,
      message: this.message,
      field: this.field,
      details: this.details,
    };
  }
}

/**
 * Authentication-specific error
 */
export class AuthError extends AppError {
  constructor(
    code: AuthErrorCode,
    message: string,
    details?: Record<string, unknown>
  ) {
    super(code, message, undefined, details);
    this.name = 'AuthError';
  }
}

/**
 * Validation-specific error
 */
export class ValidationError extends AppError {
  constructor(
    code: ValidationErrorCode,
    message: string,
    field: string,
    details?: Record<string, unknown>
  ) {
    super(code, message, field, details);
    this.name = 'ValidationError';
  }
}

/**
 * Network-specific error
 */
export class NetworkError extends AppError {
  constructor(
    code: NetworkErrorCode,
    message: string,
    details?: Record<string, unknown>
  ) {
    super(code, message, undefined, details);
    this.name = 'NetworkError';
  }
}

// =============================================================================
// Error Helper Functions
// =============================================================================

/**
 * Type guard to check if an error is an ApiError
 */
export function isApiError(error: unknown): error is ApiError {
  return (
    typeof error === 'object' &&
    error !== null &&
    'code' in error &&
    'message' in error
  );
}

/**
 * Type guard to check if an error is an AppError
 */
export function isAppError(error: unknown): error is AppError {
  return error instanceof AppError;
}

/**
 * Type guard to check if an error is an AuthError
 */
export function isAuthError(error: unknown): error is AuthError {
  return error instanceof AuthError;
}

/**
 * Type guard to check if an error is a ValidationError
 */
export function isValidationError(error: unknown): error is ValidationError {
  return error instanceof ValidationError;
}

/**
 * Type guard to check if an error is a NetworkError
 */
export function isNetworkError(error: unknown): error is NetworkError {
  return error instanceof NetworkError;
}

/**
 * Get user-friendly error message
 */
export function getErrorMessage(error: unknown): string {
  if (isAppError(error)) {
    return error.message;
  }
  if (isApiError(error)) {
    return error.message;
  }
  if (error instanceof Error) {
    return error.message;
  }
  return 'An unexpected error occurred';
}
